<template>
  <section class="drag-and-drop" :class="answerPosition">
    <div class="label-stage" :class="answerPosition">
      <ul class="drop-zone" :class="answerPosition">
        <img class="drag-and-drop-image" :src="imagePath">
        <li
          :key="index"
          v-for="(step, index) in steps"
          :style="getDropDestinationBoxStyling(step.x, step.y)"
          :correct-answers="step.correctAnswers.join(',')"
          class="drop-destination"
        >
          <label-box white :height="labelHeight" :width="getLabelWidth()" label></label-box>
        </li>
      </ul>
      <ul class="drag-zone" :class="answerPosition">
        <li v-for="label in labels" v-bind:key="label" class="drag-target">
          <label-box :label="label" :height="labelHeight" :width="getLabelWidth()"></label-box>
        </li>
      </ul>
    </div>
  </section>
</template>

<script>
import flatten from 'lodash/flatten';
import { TweenMax } from 'gsap';
import Draggable from 'gsap/Draggable';
import LabelBox from 'components/inputs/LabelBox.vue';

export default {
  components: { LabelBox },
  props: {
    answerPosition: String,
    imagePath: String,
    roundIndex: Number,
    steps: {
      type: Array,
      default: () => []
    },
    onDropLabel: {
      type: Function,
      default: () => () => {}
    }
  },

  data() {
    return {
      draggables: [],
      snappedLabels: 0,
      labelHeight: 0
    };
  },

  mounted() {
    this.$nextTick(() => {
      this.makeLabelsDraggable();
      this.setLabelHeight();
    });
  },

  computed: {
    labels() {
      const allCorrectAnswers = flatten(this.steps.map(step => step.correctAnswers));
      const uniqueCorrectAnswers = allCorrectAnswers.filter((answer, index, arr) =>
        arr.slice(0, index).indexOf(answer) === -1
      );

      return uniqueCorrectAnswers;
    }
  },

  watch: {
    imagePath(val, oldVal) {
      if (val !== oldVal) {
        this.makeLabelsDraggable();
        this.setLabelHeight();
        this.snappedLabels = 0;
        this.checkSnappedLabels(this.snappedLabels);
      }
    }
  },

  methods: {
    getAnswerDataFromDOM() {
      const dropDestinations = Array.from(document.getElementsByClassName('drop-destination'));
      const dropDestinationStates = dropDestinations.map((item) => {
        const answer = item.getAttribute('answer');
        const correctAnswers = item.getAttribute('correct-answers').split(',');
        const isCorrectAnswer = correctAnswers.indexOf(answer) !== -1;

        return {
          answer,
          correctAnswers,
          isCorrectAnswer
        };
      });

      return dropDestinationStates.filter(state => state.answer !== null);
    },

    checkSnappedLabels() {
      this.onDropLabel(
        'dragAndDrop',
        this.roundIndex,
        this.getAnswerDataFromDOM()
      );
    },

    setLabelHeight() {
      const labelLengths = this.steps.map(step => step.correctAnswers[0].length);
      const longestLabel = Math.max(...labelLengths);
      const textLineAmount = Math.ceil(longestLabel / 23);

      this.labelHeight = textLineAmount === 1 ? 50 : textLineAmount * 30;
    },

    getLabelWidth() {
      return this.answerPosition === 'bottom' ? 220 : 170;
    },

    makeLabelsDraggable() {
      const vm = this;
      const dragTargets = document.getElementsByClassName('drag-target');
      const activityStage = document.getElementsByClassName('activity-stage');

      this.draggables = Draggable.create(dragTargets, {
        bounds: activityStage,
        onDragEnd() {
          vm.handleLabelDragEnd(this);
        }
      });
    },

    handleLabelDragEnd(draggableInstance) {
      const { target: dragTarget } = draggableInstance;
      const targetAnswer = dragTarget.childNodes[0].childNodes[0].innerHTML;
      const dropDestinations = document.getElementsByClassName('drop-destination');
      const hitDropDestination = this.getHitDropDestination(dropDestinations, draggableInstance);
      const existingAnswer = hitDropDestination ? hitDropDestination.getAttribute('answer') : '';

      if (hitDropDestination) {
        if (existingAnswer) {
          this.removeExistingAnswerFromDropDestination(targetAnswer);
          this.handleValidDragEnd(hitDropDestination, draggableInstance);
        } else {
          this.handleValidDragEnd(hitDropDestination, draggableInstance);
        }
      } else {
        this.handleInvalidDragEnd(dragTarget);
      }
    },

    handleInvalidDragEnd(dragTarget) {
      const targetAnswer = dragTarget.childNodes[0].childNodes[0].innerHTML;
      this.moveLabelBackInInitialPosition(dragTarget);
      this.removeExistingAnswerFromDropDestination(targetAnswer);
    },

    handleValidDragEnd(hitDropDestination, draggableInstance) {
      const { target: dragTarget } = draggableInstance;
      const targetAnswer = dragTarget.childNodes[0].childNodes[0].innerHTML;
      hitDropDestination.setAttribute('answer', targetAnswer);
      this.snapLabelToDropDestination(hitDropDestination, draggableInstance);
      this.removeExistingAnswerFromDropDestination(
        targetAnswer,
        hitDropDestination
      );

      this.checkSnappedLabels();
    },

    removeExistingAnswerFromDropDestination(answer, dropDestinationToExclude = null) {
      const dropDestination = Array.from(
        document.getElementsByClassName('drop-destination')
      ).find(
        dropDestEl =>
          dropDestEl.getAttribute('answer') === answer &&
          dropDestEl !== dropDestinationToExclude
      );
      if (dropDestination) {
        dropDestination.removeAttribute('answer');
      }
      this.checkSnappedLabels();
    },
    /* eslint-enable */

    moveLabelBackInInitialPosition(dragTarget) {
      TweenMax.to(dragTarget, 0.5, { x: 0, y: 0 });
    },

    snapLabelToDropDestination(dropDestination, draggableInstance) {
      const { target: dragTarget } = draggableInstance;
      const diffX =
        dragTarget.getBoundingClientRect().left -
        dropDestination.getBoundingClientRect().left;
      const diffY =
        dragTarget.getBoundingClientRect().top -
        dropDestination.getBoundingClientRect().top;
      const offsetX = Math.floor(draggableInstance.x - diffX);
      const offsetY = Math.floor(draggableInstance.y - diffY);

      TweenMax.to(dragTarget, 0.5, {
        x: offsetX,
        y: offsetY
      });
    },

    getHitDropDestination(dropDestinations, draggableInstance) {
      const { target: dragTarget } = draggableInstance;
      const targetAnswer = dragTarget.childNodes[0].childNodes[0].innerHTML;
      for (let i = 0; i < dropDestinations.length; i += 1) {
        if (dropDestinations[i].getAttribute('answer') === targetAnswer) {
          dropDestinations[i].removeAttribute('answer');
        }
        if (draggableInstance.hitTest(dropDestinations[i], '60%')) {
          return dropDestinations[i];
        }
      }

      return null;
    },

    getDropDestinationBoxStyling(x, y) {
      return {
        left: `${x}px`,
        top: `${y}px`
      };
    }
  }
};
</script>

<style lang="scss" scoped>
.drag-and-drop {
  width: 100%;
  padding: 0 30px;
  flex: 1;

  &.right {
    padding: 0 60px 0 30px;
  }
}

.label-stage {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 100%;

  &.right {
    flex-direction: row;
  }

  &.bottom {
    flex-direction: column;
  }
}

ul {
  list-style: none;
  margin: 0;
}

.drag-zone {
  flex: 1;
  display: flex;

  &.right {
    height: 478px;
    flex-direction: column;
    justify-content: space-around;
    align-items: flex-end;
    padding-right: 30px;
  }

  &.bottom {
    flex-direction: row;
    justify-content: space-evenly;
    align-items: center;
    padding: 5px 0;
    width: 100%;
  }

  @supports (-ms-ime-align: auto) {
    &.bottom {
      justify-content: space-around !important;
    }
  }

  li {
    position: relative;
  }
}

.drop-zone {
  position: relative;
  &.right {
    width: 455px;
    height: 478px;
  }

  .drag-and-drop-image {
    position: absolute;
    top: 0;
    left: 0;
  }

  &.bottom {
    width: 860px;
    height: 390px;
    padding: 0;
  }

  position: relative;

  li {
    position: absolute;
  }
}
</style>
